import type { MouseEvent, VideoHTMLAttributes } from 'react'
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'
import { getMediaUrl } from 'src/utils/media'

export type VideoMediaProps = Omit<
  VideoHTMLAttributes<HTMLVideoElement>,
  'autoPlay'
> & {
  mediaId?: string
  autoPlay?: boolean | 'hover'
}

const _VideoMedia = forwardRef<HTMLVideoElement | null, VideoMediaProps>(
  function VideoMedia(props, ref) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const restProps = (({ mediaId, src: _src, ...restProps }) => restProps)(
      props,
    )
    const src = props.src || getMediaUrl(props.mediaId, 'orig')
    const shouldAutoPlay = props.autoPlay === true
    const shouldPlayOnHover = props.autoPlay === 'hover'
    const shouldMute = props.muted ?? false
    const shouldLoop = props.loop ?? true
    const playsInline = props.playsInline ?? shouldAutoPlay

    const elRef = useRef<HTMLVideoElement>(null)

    useImperativeHandle(ref, () => elRef.current!)

    // React's reconciliation of the `muted` attribute is unreliable on
    // `<video>` elements: in dev (HMR / StrictMode double mount) and on
    // some Chromium versions a JSX `muted={true}` does NOT actually
    // synchronise to `video.muted = true`, so a "muted" autoplay
    // preview ends up routing audio to the speakers anyway. Symptom on
    // /video/:id: the detail page renders the main `<VideoMedia muted=
    // {!hasAudio}>` plus 4-5 `<MediaPreview>` recommended-artwork
    // previews that all share the same source URL — each "muted"
    // preview also leaks a copy of the audio track, producing the
    // multiple overlapping audio tracks reported on cold load and
    // surviving briefly after navigating away (the orphaned decoders
    // continue playing until the browser GCs them).
    //
    // Mirror the prop into the underlying DOM property on every
    // render so the imperative `muted` setter wins over whatever
    // React managed to put on the attribute. Same trick is needed
    // for `loop` since the `loop` attribute is read once at element
    // creation and React's reconciliation of `loop` follows the same
    // unreliable path.
    useEffect(() => {
      const el = elRef.current
      if (!el) return
      if (el.muted !== shouldMute) el.muted = shouldMute
      if (el.loop !== shouldLoop) el.loop = shouldLoop
    }, [shouldMute, shouldLoop])

    const onMouseEnter = useCallback(
      (ev: MouseEvent<HTMLVideoElement>) => {
        const el = elRef.current
        if (shouldPlayOnHover && el?.buffered.length) {
          el.play()
        }
        props.onMouseEnter?.(ev)
      },
      [shouldPlayOnHover, props.onMouseEnter],
    )

    const onMouseLeave = useCallback(
      (ev: MouseEvent<HTMLVideoElement>) => {
        const el = elRef.current
        if (shouldPlayOnHover && el?.buffered.length) {
          el.pause()
          el.currentTime = 0
        }
        props.onMouseLeave?.(ev)
      },
      [shouldPlayOnHover, props.onMouseLeave],
    )

    return (
      <video
        ref={elRef}
        {...restProps}
        src={src}
        autoPlay={shouldAutoPlay}
        playsInline={playsInline}
        muted={shouldMute}
        loop={shouldLoop}
        disablePictureInPicture
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
    )
  },
)

export const VideoMedia = memo(_VideoMedia)
